const DEFAULT_WINDOW_MS = 150;
const requestEntries = new Map();

const isPlainObject = (value) =>
  Object.prototype.toString.call(value) === "[object Object]";

export const stableSerializeRequestValue = (value) => {
  if (value == null) return String(value);

  if (typeof URLSearchParams !== "undefined" && value instanceof URLSearchParams) {
    return value.toString();
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (Array.isArray(value)) {
    return `[${value.map(stableSerializeRequestValue).join(",")}]`;
  }

  if (isPlainObject(value)) {
    return `{${Object.keys(value)
      .sort()
      .map(
        (key) =>
          `${JSON.stringify(key)}:${stableSerializeRequestValue(value[key])}`,
      )
      .join(",")}}`;
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  return JSON.stringify(value);
};

export const coalesceRequestBurst = (
  key,
  requestFactory,
  { windowMs = DEFAULT_WINDOW_MS, bypass = false } = {},
) => {
  if (bypass || windowMs <= 0) {
    return Promise.resolve().then(requestFactory);
  }

  const normalizedKey = String(key ?? "");
  const now = Date.now();
  const existing = requestEntries.get(normalizedKey);

  if (existing && now - existing.startedAt <= windowMs) {
    if (
      process.env.NODE_ENV === "development" &&
      typeof localStorage !== "undefined" &&
      localStorage.getItem("debugHttpDedupe") === "true"
    ) {
      console.debug("[HTTP][DEDUP] Reusing request", normalizedKey);
    }

    return existing.promise;
  }

  const promise = Promise.resolve().then(requestFactory);
  const entry = {
    startedAt: now,
    promise,
  };

  requestEntries.set(normalizedKey, entry);

  const cleanupDelay = Math.max(1, Number(windowMs) || DEFAULT_WINDOW_MS);
  setTimeout(() => {
    if (requestEntries.get(normalizedKey) === entry) {
      requestEntries.delete(normalizedKey);
    }
  }, cleanupDelay);

  return promise;
};

export const buildRequestBurstKey = ({
  namespace,
  method,
  url,
  params,
  data,
  token,
  responseType,
  headers,
}) =>
  [
    namespace || "http",
    String(method || "GET").toUpperCase(),
    String(url || ""),
    stableSerializeRequestValue(params),
    stableSerializeRequestValue(data),
    String(token || ""),
    String(responseType || ""),
    stableSerializeRequestValue(headers),
  ].join("|");
